import {
  CONTIGUOUS_USA_PROVINCES,
  USAProvinceCode,
  usaProvinces,
} from "@redotech/locale/provinces";
import { useHandler } from "@redotech/react-util/hook";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { BadgeRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoTagsDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import PlusIcon from "@redotech/redo-web/icon-old/plus.svg";
import { Text } from "@redotech/redo-web/text";
import { isDef } from "@redotech/util/checks";
import { memo, useMemo, useState } from "react";

export const UsProvinceSelectionModal = memo(function UsProvinceSelectionModal({
  selectedProvinces,
  onProvincesSelected,
  onClose,
}: {
  selectedProvinces: USAProvinceCode[];
  onProvincesSelected: (provinces: USAProvinceCode[]) => void;
  onClose: () => void;
}) {
  const [dropdownOpen_doNotDirectlySet, setDropdownOpen_doNotDirectlySet] =
    useState(false);

  const [localSelectedProvinces, setLocalSelectedProvinces] =
    useState<USAProvinceCode[]>(selectedProvinces);

  const provinceOptions: BadgeRedoListItem<USAProvinceCode>[] = useMemo(() => {
    const seenProvinces = new Set<USAProvinceCode>();
    return usaProvinces
      .map((province) => {
        if (seenProvinces.has(province.code)) {
          return undefined;
        }
        seenProvinces.add(province.code);
        return {
          type: "badge" as const,
          id: province.code,
          value: province.code,
          badge: { text: province.name, color: "gray" as const },
        };
      })
      .filter(isDef);
  }, []);

  const selectedOptions = useMemo(() => {
    return provinceOptions.filter((option) =>
      localSelectedProvinces.includes(option.value),
    );
  }, [provinceOptions, localSelectedProvinces]);

  const handleSelectionChange = useHandler(
    (items: BadgeRedoListItem<USAProvinceCode>[]) => {
      setLocalSelectedProvinces(items.map((item) => item.value));
    },
  );

  const handleSave = useHandler(() => {
    onProvincesSelected(localSelectedProvinces);
  });

  const handleResetToContiguousStatesOnly = useHandler(() => {
    setLocalSelectedProvinces(CONTIGUOUS_USA_PROVINCES);
  });

  const handleClose = useHandler(() => {
    // If the dropdown is open, we don't want to close the modal.
    // This should give the dropdown's clickaway listener a chance to handle
    // the click event before the modal closes.
    // This is because I watched several people think they saved their work
    // without realizing that the closed the modal without saving.
    if (!dropdownOpen_doNotDirectlySet) {
      onClose();
    }
  });

  return (
    <RedoModal
      modalSize="md"
      onModalCloseRequested={handleClose}
      primaryButton={{ text: "Save", onClick: handleSave }}
      secondaryButton={{ text: "Cancel", onClick: handleClose }}
      title="Select US States"
    >
      <Flex dir="column" gap="lg" p="md">
        <RedoTagsDropdownInput<USAProvinceCode>
          label="US Provinces"
          onDropdownOpenChange={(newOpenValue) => {
            setDropdownOpen_doNotDirectlySet(newOpenValue);
          }}
          options={provinceOptions}
          placeholder="Select provinces"
          selectedOptions={selectedOptions}
          setSelectedOptions={handleSelectionChange}
          size="xs"
        />
        <Flex justify="space-between">
          <RedoButton
            hierarchy="tertiary"
            IconLeading={PlusIcon}
            onClick={handleResetToContiguousStatesOnly}
            text="Reset to contiguous states only"
          />
          <Text fontSize="xs" textColor="tertiary">
            {localSelectedProvinces.length} selected
          </Text>
        </Flex>
      </Flex>
    </RedoModal>
  );
});
